import { toZonedTime } from "date-fns-tz";
import type { TFunction } from "i18next";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";

import { InformationIcon } from "~/components/ui/icons/information";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import type { OrderQuery } from "~/graphql/graphql-new.gen";
import { TrackingSubStatus } from "~/graphql/graphql-new.gen";
import { brandState } from "~/stores/brand";
import { formatDate } from "~/utils/date";

type OrderDeliveredAtProps = {
  parcel?: OrderQuery["order"]["parcels"][0];
};

const getTrackingSubStatus = (
  trackingSubStatus: TrackingSubStatus,
  t: TFunction,
) => {
  switch (trackingSubStatus) {
    case TrackingSubStatus.DeliveredMarkedByBot:
      return t("order.status.tracking_sub_status.delivered_marked_by_bot");
    case TrackingSubStatus.DeliveredMarkedByCustomer:
      return t("order.status.tracking_sub_status.delivered_marked_by_customer");
    case TrackingSubStatus.DeliveredMarkedBySupportTeam:
      return t(
        "order.status.tracking_sub_status.delivered_marked_by_support_team",
      );
    default:
      return "";
  }
};

export const OrderDeliveredAt: React.FC<OrderDeliveredAtProps> = ({
  parcel,
}) => {
  const brand = useAtomValue(brandState);
  const { i18n, t } = useTranslation();

  if (!parcel?.trackingDeliveredAt) return null;

  const deliveredAtLabel = t("order.delivery_on", {
    date: formatDate(
      brand?.timezone
        ? toZonedTime(new Date(parcel.trackingDeliveredAt), brand.timezone)
        : new Date(parcel.trackingDeliveredAt),
      i18n.language.replace("_", "-"),
      {
        day: "numeric",
        month: "numeric",
        year: "2-digit",
      },
    ),
    interpolation: { escapeValue: false },
  });

  if (
    parcel.trackingSubStatus === TrackingSubStatus.DeliveredMarkedByBot ||
    parcel.trackingSubStatus === TrackingSubStatus.DeliveredMarkedByCustomer ||
    parcel.trackingSubStatus === TrackingSubStatus.DeliveredMarkedBySupportTeam
  ) {
    return (
      <Tooltip>
        <TooltipTrigger className="flex items-center space-x-1 text-[#767A7C]">
          <p className="cursor-default text-xs">{deliveredAtLabel}</p>
          <InformationIcon className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent sideOffset={8}>
          <p className="whitespace-pre-line p-4 text-center text-xs font-semibold">
            {getTrackingSubStatus(parcel.trackingSubStatus, t)}
          </p>
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
    );
  }

  return <p className="text-xs text-[#767A7C]">{deliveredAtLabel}</p>;
};
